import { configurationApi } from '../configurationApi';
import {
  PhysicianGroupType,
  PhysicianGroupMemberType,
  CreatePhysicianGroupRequest,
  UpdatePhysicianGroupRequest,
  AssignPhysicianGroupRequest,
} from '../types/physicianGroupType';

const physicianGroupEndpoints = configurationApi.injectEndpoints({
  endpoints: (builder) => ({
    getPhysicianGroupsByCompany: builder.query<PhysicianGroupType[], { company: string }>({
      query: ({ company }) => `/PhysicianGroups?company=${encodeURIComponent(company)}`,
      providesTags: ['PhysicianGroupList'],
    }),

    getPhysicianGroup: builder.query<PhysicianGroupType, { id: string }>({
      query: ({ id }) => `/PhysicianGroup?id=${encodeURIComponent(id)}`,
      providesTags: (result, error, { id }) => [{ type: 'PhysicianGroup', id }],
    }),

    getPhysiciansByGroup: builder.query<PhysicianGroupMemberType[], { groupId: string }>({
      query: ({ groupId }) => `/Physicians/ByGroup?groupId=${encodeURIComponent(groupId)}`,
      providesTags: (result, error, { groupId }) => [{ type: 'PhysicianGroupMembers', id: groupId }],
    }),

    createPhysicianGroup: builder.mutation<PhysicianGroupType, CreatePhysicianGroupRequest>({
      query: (body) => ({
        url: '/PhysicianGroups',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PhysicianGroupList'],
    }),

    updatePhysicianGroup: builder.mutation<PhysicianGroupType, UpdatePhysicianGroupRequest>({
      query: (body) => ({
        url: '/PhysicianGroup',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        'PhysicianGroupList',
        { type: 'PhysicianGroup', id },
      ],
    }),

    deletePhysicianGroup: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/PhysicianGroup/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PhysicianGroupList'],
    }),

    assignPhysicianToGroup: builder.mutation<{ physicianId: string; physicianGroupId: string }, AssignPhysicianGroupRequest>({
      query: (body) => ({
        url: '/Physician/AssignGroup',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { physicianGroupId }) => [
        { type: 'PhysicianGroupMembers', id: physicianGroupId },
        'Physicians',
      ],
    }),

    unassignPhysicianFromGroup: builder.mutation<{ physicianId: string; physicianGroupId: null }, { physicianId: string }>({
      query: ({ physicianId }) => ({
        url: `/Physician/UnassignGroup?physicianId=${encodeURIComponent(physicianId)}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['PhysicianGroupMembers', 'Physicians'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPhysicianGroupsByCompanyQuery,
  useGetPhysicianGroupQuery,
  useGetPhysiciansByGroupQuery,
  useCreatePhysicianGroupMutation,
  useUpdatePhysicianGroupMutation,
  useDeletePhysicianGroupMutation,
  useAssignPhysicianToGroupMutation,
  useUnassignPhysicianFromGroupMutation,
} = physicianGroupEndpoints;
